import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { FinanceTab } from '@/types/columns';
import Balance from './balance';
import AdvanceCodTab from './advanceCod';
import CapitalTab from './capital';
import { useFetchCapitalSummary } from '@/hooksApi';
import useUserStore from '@/stores/userStore';
import { Landmark } from 'lucide-react';
import {
  BottomSheet,
  ExportIcon,
  Heading,
  InvoiceIcon,
  TableAction,
  PageLayout,
} from '@/components';
import { getColumns } from './list';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useRemittanceData from '@/hooks/useRemittanceData';
import { scrollTop } from '@/utils';
import useLanguageStore from '@/stores/useLanguageStore';
import { usePollingMechanismStore, useTransactionFilteringStore } from '@/stores';
import { ProcessNameEnum } from '@/stores/pollingMechanismStore';
import { useOpenEntityTicketModal } from '@/hooks';
import { TicketEntityIDFieldEnum } from '@/constants';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import {
  Wallet,
  Receipt,
  ArrowLeftRight,
  ArrowUpDown,
  Calendar,
  ChevronDown,
  Tag,
} from 'lucide-react';
import type { MenuProps } from 'antd';
import { TransactionTypeEnum } from '@/stores/useTransactionFilteringStore';

const pageSize = 25;

/**
 * Filter buttons for remittance tab — status filter is functional, date filter is placeholder.
 */
const REMITTANCE_STATUSES = ['requested', 'initiated', 'ready_to_pickup', 'delivered', 'cancelled'];

function TableFilterButtons({
  t,
  statusFilter,
  onStatusChange,
  dateFrom,
  dateTo,
  onDateChange,
}: {
  t: (key: string) => string;
  statusFilter?: string;
  onStatusChange: (status?: string) => void;
  dateFrom?: string;
  dateTo?: string;
  onDateChange?: (from?: string, to?: string) => void;
}) {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [localFrom, setLocalFrom] = useState(dateFrom ?? '');
  const [localTo, setLocalTo] = useState(dateTo ?? '');
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const dateDropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(e.target as Node)) {
        setShowDateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className='flex items-center gap-2 pb-2'>
      {/* Status filter — functional dropdown */}
      <div className='relative' ref={dropdownRef}>
        <button
          type='button'
          onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[0.50rem] border text-[12px] font-semibold cursor-pointer transition-colors ${
            statusFilter
              ? 'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]'
              : 'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]'
          }`}
        >
          <ArrowUpDown className='w-3.5 h-3.5' />
          {statusFilter ? String(t(statusFilter)) : String(t('status'))}
          <ChevronDown className='w-3.5 h-3.5' />
        </button>
        {showStatusDropdown && (
          <div className='absolute top-full start-0 mt-1 z-50 bg-white rounded-lg border border-[rgb(232,230,227)] shadow-lg py-1 min-w-[160px]'>
            <button
              type='button'
              onClick={() => {
                onStatusChange(undefined);
                setShowStatusDropdown(false);
              }}
              className={`w-full text-start px-3 py-2 text-[12px] cursor-pointer hover:bg-[rgb(245,245,243)] ${!statusFilter ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
            >
              {String(t('all'))}
            </button>
            {REMITTANCE_STATUSES.map((s) => (
              <button
                key={s}
                type='button'
                onClick={() => {
                  onStatusChange(s);
                  setShowStatusDropdown(false);
                }}
                className={`w-full text-start px-3 py-2 text-[12px] cursor-pointer hover:bg-[rgb(245,245,243)] ${statusFilter === s ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
              >
                {String(t(s))}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Date filter */}
      <div className='relative' ref={dateDropdownRef}>
        <button
          type='button'
          onClick={() => setShowDateDropdown(!showDateDropdown)}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[0.50rem] border text-[12px] font-semibold cursor-pointer transition-colors ${
            dateFrom || dateTo
              ? 'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]'
              : 'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]'
          }`}
        >
          <Calendar className='w-3.5 h-3.5' />
          {dateFrom || dateTo ? `${dateFrom || '...'} — ${dateTo || '...'}` : String(t('date'))}
          <ChevronDown className='w-3.5 h-3.5' />
        </button>
        {showDateDropdown && (
          <div className='absolute top-full right-0 mt-1 z-50 bg-white rounded-lg border border-[rgb(232,230,227)] shadow-lg p-3 min-w-[220px]'>
            <p className='text-[10px] font-bold uppercase tracking-wider mb-2 text-[rgb(115,123,140)]'>
              {String(t('date_range'))}
            </p>
            <div className='space-y-2'>
              <div>
                <label className='text-[10px] block mb-1 text-[rgb(115,123,140)]'>
                  {String(t('date_from'))}
                </label>
                <input
                  type='date'
                  value={localFrom}
                  onChange={(e) => setLocalFrom(e.target.value)}
                  className='w-full h-8 px-2 rounded-lg border border-solid text-[12px] focus:outline-none'
                  style={{ borderColor: 'rgb(232,230,227)', color: 'rgb(57,57,60)' }}
                />
              </div>
              <div>
                <label className='text-[10px] block mb-1 text-[rgb(115,123,140)]'>
                  {String(t('date_to'))}
                </label>
                <input
                  type='date'
                  value={localTo}
                  onChange={(e) => setLocalTo(e.target.value)}
                  className='w-full h-8 px-2 rounded-lg border border-solid text-[12px] focus:outline-none'
                  style={{ borderColor: 'rgb(232,230,227)', color: 'rgb(57,57,60)' }}
                />
              </div>
              <div className='flex items-center gap-2 pt-1'>
                {(localFrom || localTo || dateFrom || dateTo) && (
                  <button
                    onClick={() => {
                      setLocalFrom('');
                      setLocalTo('');
                      onDateChange?.(undefined, undefined);
                    }}
                    className='text-[11px] font-semibold h-7 bg-transparent border-0 cursor-pointer p-0 hover:underline'
                    style={{ color: 'hsl(0,72%,51%)' }}
                  >
                    {String(t('clear_all'))}
                  </button>
                )}
                <div className='flex-1' />
                <button
                  onClick={() => {
                    onDateChange?.(localFrom || undefined, localTo || undefined);
                    setShowDateDropdown(false);
                  }}
                  className='h-7 px-3 rounded-md text-[11px] font-semibold cursor-pointer border-0'
                  style={{ background: 'rgb(237,224,74)', color: 'rgb(57,57,60)' }}
                >
                  {String(t('apply'))}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TRANSACTION_STATUSES = ['enabled', 'pending', 'paid'] as const;
const TRANSACTION_TYPES = [
  { key: TransactionTypeEnum.WITHDRAW, label: 'withdraw' },
  { key: TransactionTypeEnum.DEPOSIT, label: 'deposit' },
];

/* Date filter for Transactions tab */
function TransactionDateFilter({
  t,
  dateFrom,
  dateTo,
  onChange,
}: {
  t: (key: string) => string;
  dateFrom?: string;
  dateTo?: string;
  onChange: (from?: string, to?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(dateFrom ?? '');
  const [localTo, setLocalTo] = useState(dateTo ?? '');
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasFilter = !!(dateFrom || dateTo);

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={(e) => {
          e.preventDefault();
          setOpen(!open);
        }}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[0.50rem] border text-[12px] font-semibold cursor-pointer transition-colors ${
          hasFilter
            ? 'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]'
            : 'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]'
        }`}
      >
        <Calendar className='w-3.5 h-3.5' />
        {hasFilter ? `${dateFrom || '...'} — ${dateTo || '...'}` : String(t('date'))}
        <ChevronDown className='w-3.5 h-3.5' />
      </button>
      {open && (
        <div
          className='absolute top-full end-0 mt-1 z-50 bg-white rounded-lg border border-[rgb(232,230,227)] shadow-lg p-3 min-w-[220px]'
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '80vh', overflowY: 'auto' }}
        >
          <p className='text-[10px] font-bold uppercase tracking-wider mb-2 text-[rgb(115,123,140)]'>
            {String(t('date_range'))}
          </p>
          <div className='space-y-2'>
            <div>
              <label className='text-[10px] block mb-1 text-[rgb(115,123,140)]'>
                {String(t('date_from'))}
              </label>
              <input
                type='date'
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                className='w-full h-8 px-2 rounded-lg border border-solid text-[12px] focus:outline-none'
                style={{ borderColor: 'rgb(232,230,227)', color: 'rgb(57,57,60)' }}
              />
            </div>
            <div>
              <label className='text-[10px] block mb-1 text-[rgb(115,123,140)]'>
                {String(t('date_to'))}
              </label>
              <input
                type='date'
                value={localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                className='w-full h-8 px-2 rounded-lg border border-solid text-[12px] focus:outline-none'
                style={{ borderColor: 'rgb(232,230,227)', color: 'rgb(57,57,60)' }}
              />
            </div>
            <div className='flex items-center gap-2 pt-1'>
              {(localFrom || localTo || hasFilter) && (
                <button
                  onClick={() => {
                    setLocalFrom('');
                    setLocalTo('');
                    onChange(undefined, undefined);
                  }}
                  className='text-[11px] font-semibold h-7 bg-transparent border-0 cursor-pointer p-0 hover:underline'
                  style={{ color: 'hsl(0,72%,51%)' }}
                >
                  {String(t('clear_all'))}
                </button>
              )}
              <div className='flex-1' />
              <button
                onClick={() => {
                  onChange(localFrom || undefined, localTo || undefined);
                  setOpen(false);
                }}
                className='h-7 px-3 rounded-md text-[11px] font-semibold cursor-pointer border-0'
                style={{ background: 'rgb(237,224,74)', color: 'rgb(57,57,60)' }}
              >
                {String(t('apply'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Reusable inline dropdown for Transaction filters */
function TransactionFilterDropdown<T extends string>({
  icon,
  label,
  value,
  options,
  onChange,
  allLabel,
}: {
  icon: React.ReactNode;
  label: string;
  value?: T;
  options: Array<{ key: T; label: string }>;
  onChange: (v?: T) => void;
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = !!value;

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-[0.50rem] border text-[12px] font-semibold cursor-pointer transition-colors ${
          isActive
            ? 'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]'
            : 'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]'
        }`}
      >
        {icon}
        {value ? (options.find((o) => o.key === value)?.label ?? label) : label}
        <ChevronDown className='w-3.5 h-3.5' />
      </button>
      {open && (
        <div className='absolute top-full start-0 mt-1 z-50 bg-white rounded-xl border border-[rgb(232,230,227)] shadow-lg py-1.5 min-w-[170px]'>
          <button
            type='button'
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
            className={`w-full text-start px-3.5 py-2 text-[12px] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors ${!value ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
          >
            {allLabel}
          </button>
          {options.map((opt) => (
            <button
              key={opt.key}
              type='button'
              onClick={() => {
                onChange(opt.key);
                setOpen(false);
              }}
              className={`w-full text-start px-3.5 py-2 text-[12px] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors ${value === opt.key ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Finance = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { startPolling } = usePollingMechanismStore();
  const hasExportProcess = usePollingMechanismStore((state) =>
    state.processes.has(ProcessNameEnum.EXPORT_STATEMENT),
  );
  const [activeTabParams, setActiveTabParams] = useSearchParams();
  const [downloadingUid, setDownloadingUid] = useState<string | null>(null);

  const { language } = useLanguageStore();
  // Advance COD tab is listed only when the merchant has a financing row
  // (GET /user advance_cod block, null = hide) AND the user holds the
  // ADVANCE_COD permission — mirrors the Sidebar gate and the RBAC that
  // protected the old standalone /advance-cod route. Without this a sub-user
  // with FINANCE but not ADVANCE_COD would see the tab and 403 on every
  // advance-cod.* API call (config/merchant_route_permissions.php).
  const user = useUserStore((state) => state.user);
  const showAdvanceCodTab = !!user?.advance_cod && hasPermission(PermissionName.ADVANCE_COD);

  // Boxy Capital: the tab appears only for a merchant with an advance
  // (summary object non-null). Money surface → same FINANCE gate the page
  // itself sits behind; the summary query is cheap and cached 60s.
  const { data: capitalSummary } = useFetchCapitalSummary();
  const showCapitalTab = !!capitalSummary?.object;

  const {
    createdAtFilter,
    statusFilter: transactionStatusFilter,
    typeFilter: transactionTypeFilter,
    setStatus: setTransactionStatus,
    setType: setTransactionType,
    setCreatedAt,
  } = useTransactionFilteringStore();

  const { openEntityTicketModal } = useOpenEntityTicketModal(TicketEntityIDFieldEnum.REMITTANCE);

  // Function to get tab from URL parameter
  const getTabFromUrl = (tabParam: string | null): FinanceTab => {
    return tabParam === FinanceTab.COD
      ? FinanceTab.COD
      : tabParam === FinanceTab.Transactions
        ? FinanceTab.Transactions
        : tabParam === FinanceTab.AdvanceCod && showAdvanceCodTab
          ? FinanceTab.AdvanceCod
          : tabParam === FinanceTab.Capital && showCapitalTab
            ? FinanceTab.Capital
            : FinanceTab.Balance;
  };

  // dropdown items bottomsheet
  const menuItems: MenuProps['items'] = [
    {
      key: '1',
      label: (
        <Heading title={String(t('export_csv'))} leadingIcon={<ExportIcon />} rotation={false} />
      ),
    },
  ];

  // handle active checkbox Item
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  // remittance status + date filters
  const [remittanceStatusFilter, setRemittanceStatusFilter] = useState<string | undefined>();
  const [remittanceDateFrom, setRemittanceDateFrom] = useState<string | undefined>();
  const [remittanceDateTo, setRemittanceDateTo] = useState<string | undefined>();

  // find active tab
  const [activeTab, setActiveTab] = useState<FinanceTab>(() => {
    const tabParam = activeTabParams.get('tab');
    return getTabFromUrl(tabParam);
  });

  // Initialize tab from URL on mount and when URL changes
  useEffect(() => {
    const tabParam = activeTabParams.get('tab');
    const newTab = getTabFromUrl(tabParam);

    // Only update if the tab has changed
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [activeTabParams, activeTab]);

  const handleChangeTab = (newTab: FinanceTab) => {
    // Reset pagination
    setCurrentPage(1);

    // Create new URLSearchParams to reset all parameters
    const newActiveTabParams = new URLSearchParams();

    // Set the tab parameter for all tabs except Balance
    if (newTab !== FinanceTab.Balance) {
      newActiveTabParams.set('tab', newTab);
    }

    // Update URL with only the tab parameter
    setActiveTabParams(newActiveTabParams);

    // Update local state
    setActiveTab(newTab);
  };

  // fetch data
  const {
    dataRemittance,
    fetchingTransaction,
    isFetching,
    totals,
    totalTransactions,
    dataRemittanceTransactions,
  } = useRemittanceData(
    currentPage,
    activeTab,
    pageSize,
    language,
    {
      statusFilter: transactionStatusFilter,
      typeFilter: transactionTypeFilter,
      createdAtFilter,
    },
    remittanceStatusFilter,
    remittanceDateFrom,
    remittanceDateTo,
  );

  // Tab definitions with icons matching the redesign. Advance COD sits right
  // after Balance (design order) and appears only for enrolled merchants.
  const tabItems = [
    {
      key: FinanceTab.Balance,
      title: String(t('balance')),
      icon: <Wallet className='w-3.5 h-3.5' />,
    },
    ...(showAdvanceCodTab
      ? [
          {
            key: FinanceTab.AdvanceCod,
            title: String(t('advance_cod')),
            icon: <Landmark className='w-3.5 h-3.5' />,
          },
        ]
      : []),
    ...(showCapitalTab
      ? [
          {
            key: FinanceTab.Capital,
            title: String(t('capital')),
            icon: <Landmark className='w-3.5 h-3.5' />,
          },
        ]
      : []),
    {
      key: FinanceTab.COD,
      title: String(t('remittance')),
      icon: <Receipt className='w-3.5 h-3.5' />,
    },
    {
      key: FinanceTab.Transactions,
      title: String(t('transactions')),
      icon: <ArrowLeftRight className='w-3.5 h-3.5' />,
    },
  ];

  // handle onChange pagination
  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset downloadingUid when polling process is removed (timeout, completion, etc.)
  useEffect(() => {
    if (downloadingUid && !hasExportProcess) {
      setDownloadingUid(null);
    }
  }, [downloadingUid, hasExportProcess]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [activeTab]);

  useEffect(() => {
    scrollTop();
  }, [currentPage]);

  // Determine active data totals for the info bar
  const activeTotal =
    activeTab === FinanceTab.COD
      ? totals
      : activeTab === FinanceTab.Transactions
        ? totalTransactions
        : undefined;

  // Derive a label for the info bar count
  const activeTotalLabel =
    activeTab === FinanceTab.COD
      ? String(t('cod_remittances')).toLowerCase()
      : activeTab === FinanceTab.Transactions
        ? String(t('transactions')).toLowerCase()
        : '';

  // Filter buttons are rendered inline in the tab bar (right-aligned)

  return (
    <PageLayout>
      <div className='flex flex-col w-full'>
        {/* Underline tab bar — matches redesign exactly */}
        <div className='flex items-end w-full border-b border-[rgb(232,230,227)] -mx-0 px-0'>
          {tabItems.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type='button'
                onClick={() => handleChangeTab(tab.key as FinanceTab)}
                className={[
                  'flex items-center gap-2 px-5 pb-2.5 pt-3 text-[13px] font-semibold -mb-px whitespace-nowrap transition-colors cursor-pointer bg-transparent border-0 border-b-2 border-solid',
                  isActive
                    ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]'
                    : 'border-b-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]',
                ].join(' ')}
              >
                {tab.icon}
                {tab.title}
              </button>
            );
          })}
          <div className='flex-1' />
          {/* Filter buttons — right-aligned in the tab row (only on table tabs) */}
          {activeTab === FinanceTab.COD && (
            <TableFilterButtons
              t={t}
              statusFilter={remittanceStatusFilter}
              onStatusChange={(s) => {
                setRemittanceStatusFilter(s);
                setCurrentPage(1);
              }}
              dateFrom={remittanceDateFrom}
              dateTo={remittanceDateTo}
              onDateChange={(from, to) => {
                setRemittanceDateFrom(from);
                setRemittanceDateTo(to);
                setCurrentPage(1);
              }}
            />
          )}
          {activeTab === FinanceTab.Transactions && (
            <div className='flex items-center gap-2 pb-2'>
              {/* Status filter (primary) */}
              <TransactionFilterDropdown
                icon={<ArrowUpDown className='w-3.5 h-3.5' />}
                label={String(t('status'))}
                value={
                  transactionStatusFilter.length === 1 ? transactionStatusFilter[0] : undefined
                }
                options={TRANSACTION_STATUSES.map((s) => ({
                  key: s,
                  label: String(
                    t(`transaction_status.${s}`, {
                      defaultValue: s.charAt(0).toUpperCase() + s.slice(1),
                    }),
                  ),
                }))}
                onChange={(v) => {
                  setTransactionStatus(v ? [v as any] : []);
                  setCurrentPage(1);
                }}
                allLabel={String(t('all_statuses', { defaultValue: 'All Statuses' }))}
              />
              {/* Type filter */}
              <TransactionFilterDropdown
                icon={<Tag className='w-3.5 h-3.5' />}
                label={String(t('type', { defaultValue: 'Type' }))}
                value={transactionTypeFilter}
                options={TRANSACTION_TYPES.map((tp) => ({
                  key: tp.key,
                  label: String(
                    t(tp.label, {
                      defaultValue: tp.label.charAt(0).toUpperCase() + tp.label.slice(1),
                    }),
                  ),
                }))}
                onChange={(v) => {
                  setTransactionType(v);
                  setCurrentPage(1);
                }}
                allLabel={String(t('all_types', { defaultValue: 'All Types' }))}
              />
              {/* Date filter for transactions */}
              <TransactionDateFilter
                t={t}
                dateFrom={createdAtFilter?.[0]?.format('YYYY-MM-DD')}
                dateTo={createdAtFilter?.[1]?.format('YYYY-MM-DD')}
                onChange={(from, to) => {
                  if (from || to) {
                    const dayFrom = from ? dayjs(from) : dayjs().subtract(1, 'month');
                    const dayTo = to ? dayjs(to) : dayjs();
                    setCreatedAt([dayFrom, dayTo]);
                  } else {
                    setCreatedAt(undefined);
                  }
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </div>

        {/* Content: Balance view or data table */}
        <div className='mb-24 mt-4'>
          {activeTab === FinanceTab.Balance ? (
            <Balance setActiveTab={handleChangeTab} />
          ) : activeTab === FinanceTab.Capital ? (
            <CapitalTab />
          ) : activeTab === FinanceTab.AdvanceCod ? (
            <AdvanceCodTab />
          ) : (
            <Row className='w-full'>
              <Col md={24} xs={24}>
                <TableAction
                  columns={getColumns(
                    activeTab,
                    t,
                    downloadingUid,
                    setDownloadingUid,
                    openEntityTicketModal,
                    hasPermission,
                    startPolling,
                  )}
                  selectedRowKeys={selectedRowKeys}
                  setSelectedRowKeys={setSelectedRowKeys}
                  data={FinanceTab.COD === activeTab ? dataRemittance : dataRemittanceTransactions}
                  pageSize={pageSize}
                  current={currentPage}
                  total={FinanceTab.COD === activeTab ? totals : totalTransactions}
                  loading={FinanceTab.Transactions === activeTab ? fetchingTransaction : isFetching}
                  onPaginationChange={handlePaginationChange}
                  rowSelectionEnabled={false}
                  onRow={
                    activeTab === FinanceTab.COD
                      ? (record) => ({
                          onClick: () => navigate(`/finance/details/${record.key}`),
                          style: { cursor: 'pointer' },
                        })
                      : undefined
                  }
                />
              </Col>
            </Row>
          )}
        </div>
      </div>

      {/* BottomSheet for bulk export (Transactions tab) */}
      {FinanceTab.Transactions === activeTab && (
        <BottomSheet
          isVisible={selectedRowKeys.length > 0}
          numberSelected={selectedRowKeys.length}
          total={totals}
          labelBtn={`${String(t('export_csv'))} (11)`}
          Icon={<InvoiceIcon />}
          menuItems={menuItems}
        />
      )}
    </PageLayout>
  );
};

export default Finance;
